import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { userService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  Modal, FormGroup, Input, Select, Badge,
  PageHeader, EmptyState, Loading, Table
} from '../components/UI';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Administrador' },
  { value: 'manager', label: 'Gerente' },
  { value: 'operator', label: 'Operador' },
  { value: 'viewer', label: 'Visualizador' },
];

const emptyForm = {
  name: '',
  email: '',
  password: '',
  role: 'operator',
  is_active: true,
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const res = await userService.list({ limit: 200 });
      const data = Array.isArray(res) ? res : res.data ?? [];
      setUsers(data);
    } catch {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: '', role: u.role, is_active: u.is_active });
    setModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error('Nome e e-mail são obrigatórios');
    if (!editing && !form.password) return toast.error('Senha obrigatória para novo usuário');
    try {
      setSaving(true);
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (editing) {
        await userService.update(editing.id, payload);
        toast.success('Usuário atualizado');
      } else {
        await userService.create(payload);
        toast.success('Usuário criado');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    if (u.id === currentUser.id) return toast.error('Não é possível desativar seu próprio usuário');
    try {
      await userService.update(u.id, { is_active: !u.is_active });
      toast.success(u.is_active ? 'Usuário desativado' : 'Usuário ativado');
      load();
    } catch {
      toast.error('Erro ao atualizar status');
    }
  };

  const filtered = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const roleLabel = (role) => ROLE_OPTIONS.find(r => r.value === role)?.label || role;

  const columns = [
    { key: 'name', label: 'Nome' },
    { key: 'email', label: 'E-mail' },
    {
      key: 'role', label: 'Perfil',
      render: (u) => <Badge status={u.role}>{roleLabel(u.role)}</Badge>
    },
    {
      key: 'is_active', label: 'Status',
      render: (u) => (
        <Badge status={u.is_active ? 'ativo' : 'inativo'}>
          {u.is_active ? 'Ativo' : 'Inativo'}
        </Badge>
      )
    },
    {
      key: 'actions', label: '',
      render: (u) => (
        <div className="table-actions">
          <button className="btn btn-sm btn-secondary" onClick={() => openEdit(u)}>Editar</button>
          {u.id !== currentUser.id && (
            <button
              className={`btn btn-sm ${u.is_active ? 'btn-danger' : 'btn-success'}`}
              onClick={() => toggleActive(u)}
            >
              {u.is_active ? 'Desativar' : 'Ativar'}
            </button>
          )}
        </div>
      )
    }
  ];

  if (loading) return <Loading />;

  return (
    <div className="page">
      <PageHeader
        title="Usuários"
        subtitle={`${users.length} usuário${users.length !== 1 ? 's' : ''} cadastrado${users.length !== 1 ? 's' : ''}`}
        action={<button className="btn btn-primary" onClick={openNew}>+ Novo Usuário</button>}
      />

      <div className="filters-bar">
        <input
          className="input search-input"
          placeholder="Buscar por nome ou e-mail..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title="Nenhum usuário encontrado"
          description="Crie o primeiro usuário da sua equipe."
          action={<button className="btn btn-primary" onClick={openNew}>+ Novo Usuário</button>}
        />
      ) : (
        <Table columns={columns} data={filtered} />
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar Usuário' : 'Novo Usuário'}
        size="sm"
      >
        <form onSubmit={handleSubmit}>
          <FormGroup label="Nome completo" required>
            <Input name="name" value={form.name} onChange={handleChange} required />
          </FormGroup>
          <FormGroup label="E-mail" required>
            <Input name="email" type="email" value={form.email} onChange={handleChange} required />
          </FormGroup>
          <FormGroup label={editing ? 'Nova senha (deixe em branco para manter)' : 'Senha'} required={!editing}>
            <Input name="password" type="password" value={form.password} onChange={handleChange} />
          </FormGroup>
          <FormGroup label="Perfil de acesso">
            <Select name="role" value={form.role} onChange={handleChange}>
              {ROLE_OPTIONS.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </Select>
          </FormGroup>
          {editing && (
            <FormGroup label="">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={form.is_active}
                  onChange={handleChange}
                  disabled={editing?.id === currentUser.id}
                />
                <span>Usuário ativo</span>
              </label>
            </FormGroup>
          )}
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
